import React, { Suspense, useEffect, useLayoutEffect } from "react";
import "./App.css";
import {
  ChakraProvider,
  Box,
  Spinner,
  Center,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { ChevronUpIcon } from "@chakra-ui/icons";
import theme from "./theme";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import TNC from "./components/TNC"; // TNC 組件
import IntroVideo from "./components/IntroVideo"; // IntroVideo 組件
import Home from "./components/Home"; // Home 組件
import Header from "./components/Header"; // Header 組件
import Privacy from "./components/Privacy";
import About from "./components/About";
import DelAccount from "./components/DelAccount";
import Tutorial from "./components/Tutorial";
import DiaryApp from "./components/DiaryApp";
import NotFound from "./components/NotFound";
import LocalizedSite from "./components/LocalizedSite";
import { newsData } from "./data/newsData";
import siteRoutes from "./i18n/siteRoutes.json";
import { DEFAULT_LOCALE, pathForLocale, SITE_LOCALES } from "./i18n/localizedRoutes";
import News from "./components/News";
import NewsDetail from "./components/NewsDetail";
import Zodiac from "./components/Zodiac";
import Explore from "./components/Explore";
import Hexaco from "./components/Hexaco";
import HexacoPurchasePage from "./components/hexaco/HexacoPurchasePage";
import HexacoOrderStatus from "./components/hexaco/HexacoOrderStatus";
import HexacoOrderSuccess from "./components/hexaco/HexacoOrderSuccess";
import HexacoOrderCancel from "./components/hexaco/HexacoOrderCancel";
// import Souli from "./components/Souli";

// Import i18n configuration
import "./i18n";

// Import Firebase configuration
import { logWebsiteVisit } from "./firebase/config";
import LanguageSelector from "./components/LanguageSelector";
import RouteScrollManager from "./components/RouteScrollManager";
import {
  applyHexacoApiEnvFromSearch,
  clearHexacoSessionForApiEnvChange,
  isHexacoApiEnvSwitchEnabled,
} from "./constants/hexacoApiEnv";

// Loading component to show while translations are loading
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color="primary.600" />
  </Center>
);

/** Optional HEXACO ?api=staging|prod sync — remove with hexacoApiEnv.ts when retiring the switch. */
const HexacoApiEnvSync: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (!isHexacoApiEnvSwitchEnabled) {
      return;
    }

    const result = applyHexacoApiEnvFromSearch(location.search);
    if (!result.didConsumeParam) {
      return;
    }

    if (result.changed) {
      clearHexacoSessionForApiEnvChange();
    }

    if (result.nextSearch !== location.search) {
      navigate(
        {
          pathname: location.pathname,
          search: result.nextSearch,
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  return null;
};

// Scroll to top function
const ScrollToTop = () => {
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <IconButton
      aria-label="Scroll to top"
      icon={<ChevronUpIcon />}
      position="fixed"
      bottom="20px"
      right="20px"
      zIndex={999}
      size="lg"
      rounded="full"
      onClick={handleScrollToTop}
      bg="white"
      _hover={{ bg: "gray.100" }}
      boxShadow="md"
    />
  );
};

// 這裡定義 SECTIONS，確保其正確導出
export const SECTIONS = {
  HERO: "hero",
  ABOUT: "about",
  TUTORIAL: "tutorial",
  FEATURES: "features",
  FOUNDER: "founder",
  FAQ: "faq",
  LEGAL: "legal",
  CONTACT: "contact",
  PRIVACY: "privacy",
};

const LegacyPublicRoute = () => {
  const location = useLocation();
  const exists = siteRoutes.publicPaths.includes(location.pathname) ||
    newsData.some(item => location.pathname === `/news/${item.id}`);
  if (!exists) return <NotFound />;
  return <Navigate replace to={pathForLocale(location.pathname, DEFAULT_LOCALE) + location.search + location.hash} />;
};

const AboutPage = () => {
  return (
    <Box id="company-story" scrollMarginTop="88px">
      <About />
    </Box>
  );
};

// Main App component
function App() {
  useEffect(() => {
    logWebsiteVisit();
  }, []);

  const location = useLocation(); // 獲取當前路由位置

  return (
    <HelmetProvider>
      <Suspense fallback={<LoadingFallback />}>
        <ChakraProvider theme={theme}>
          <Box minH="100vh" width="100%">
            <RouteScrollManager />
            <HexacoApiEnvSync />
            <Header />
            {/* {location.pathname == "/" && <Header />}  */}
            {location.pathname === "delAccount" && (
              <Flex justify="flex-end" mt={"10px"} mr={"10px"}>
                <LanguageSelector />
              </Flex>
            )}
            <Routes>
              {/* Payment, order and account URLs retain their existing paths. */}
              <Route path="/delAccount" element={<DelAccount />} />
              <Route path="/hexaco/purchase" element={<HexacoPurchasePage />} />
              <Route path="/hexaco/order/success" element={<HexacoOrderSuccess />} />
              <Route path="/hexaco/order/cancel" element={<HexacoOrderCancel />} />
              <Route path="/hexaco/order/:orderId" element={<HexacoOrderStatus />} />
              {SITE_LOCALES.map(locale => (
                <Route key={locale} path={`/${locale}`} element={<LocalizedSite locale={locale} />}>
                  <Route index element={<Home />} />
                  <Route path="tnc" element={<TNC />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="introVideo" element={<IntroVideo />} />
                  <Route path="tutorial" element={<Tutorial />} />
                  <Route path="diary-app" element={<DiaryApp />} />
                  <Route path="news" element={<News />} />
                  <Route path="news/:id" element={<NewsDetail />} />
                  <Route path="explore" element={<Explore />} />
                  <Route path="zodiac" element={<Zodiac />} />
                  <Route path="hexaco" element={<Hexaco />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              ))}
              <Route path="*" element={<LegacyPublicRoute />} />
            </Routes>
            {/* <ScrollToTop />  */}
          </Box>
        </ChakraProvider>
      </Suspense>
    </HelmetProvider>
  );
}

// 包裝 Router 以獲取 location
const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;

